import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  FolderTree,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Problem-aware (business cohort) content piece targeting the OKR stakeholder
 * who has not yet decided the answer is an "AI file organizer". Primary query:
 * "how to standardize file naming conventions across a team" and the variant
 * "how to enforce a document naming convention that people actually follow".
 *
 * This is the document-consistency / data-governance OKR ("95%+ of new files
 * match the standard schema", "zero unfiled buckets"). The wedge: a written
 * naming policy fails because enforcement depends on every human remembering
 * it under deadline. Sortio Teams moves enforcement to the point of filing via
 * a shared rule, so the convention holds regardless of who drops files in.
 *
 * AEO-first: the title question is an H2 verbatim, answered directly in the
 * first two sentences, then numbered steps, then specifics. Tracked as a
 * "business" prompt in api/src/scripts/llm-recommendation-tracker.ts so we can
 * measure citation lift after publishing.
 */

const PUBLISHED = '2026-05-27';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/enforce-file-naming-convention';

const FAQ = [
  {
    question: 'How do you get employees to actually follow a file naming convention?',
    answer:
      'You stop relying on them to remember it. A written naming policy in a wiki fails because compliance depends on every person recalling the format correctly while under deadline, and a new hire has no way to know the convention on day one. The conventions that hold are the ones enforced at the point of filing: a tool reads each incoming file and writes the name and folder from a shared template, so the output is identical no matter who did the work. Treat the convention as something the system applies, not something people opt into.',
  },
  {
    question: 'What is a good file naming convention for a team?',
    answer:
      'Lead with an ISO date (YYYY-MM-DD) so files sort chronologically in any file browser, then the entity the file belongs to (client, matter, property, project), then a short type, then an optional disambiguator. For example 2026-04-12_Acme-Corp_Invoice_4471.pdf or 2026-04_Northgate-Apts_Lease_Unit-3B.pdf. Use hyphens inside a field and underscores between fields so each field is machine-parseable. Avoid spaces, slashes, and client-specific abbreviations that only one person understands.',
  },
  {
    question: 'How do you standardize file names across an existing messy shared drive?',
    answer:
      'Do it in two phases. First, run a one-time cleanup pass over the existing backlog: point the tool at the drive, have it read each file and propose a name and folder from your template, review the preview, then apply. Second, put a standing rule on the intake folder so every new file is named on arrival and the drive never drifts back into chaos. The backlog pass is the painful part people avoid for years; reading file content with an LLM is what makes it tractable instead of a quarter of manual renaming.',
  },
  {
    question: 'Can you enforce a naming convention automatically on Mac and Windows?',
    answer:
      'Yes. Sortio runs on both macOS and Windows as a desktop app and can watch a folder so files dropped there are renamed and routed to your convention without anyone opening the app. Team rules are shared across seats from an admin console, so every machine applies the same template. This is the difference between a convention that exists on paper and one that exists in the actual file names.',
  },
  {
    question: 'How is this different from a SharePoint or DMS naming policy?',
    answer:
      'A document management system can require metadata fields and can validate names against a pattern, but it still depends on a person typing the right values, and it only governs files that make it into the DMS. The reality for most teams is a long tail of files on local drives, in Downloads, and in shared folders that never get the metadata treatment. Sortio reads the file content and derives the fields itself (vendor, date, client, document type) rather than asking a human to enter them, and it works on plain folders, not just inside a DMS. The two are complementary: Sortio gets files into a clean, consistent state that a DMS can then ingest.',
  },
  {
    question: 'Should the convention be enforced with a fixed rule or with AI?',
    answer:
      'Both, depending on the file. For intake that always arrives in the same format (one vendor, one upstream system), promote the convention to a deterministic rule in the AI Rule Builder. It does not use your AI allowance at all and never varies. For mixed intake where the format differs per sender, use AI sorting, which reads each file and decides the fields per document. Most teams run a deterministic rule for their highest-volume single-source flow and AI sorting for everything else.',
  },
  {
    question: 'Does enforcing a naming convention help with audit readiness?',
    answer:
      'Directly. When every file is named to the same date-entity-type template and routed to the right folder at creation, assembling a complete client or matter package becomes a folder lookup instead of a multi-day hunt, and there is no "miscellaneous" pile hiding misfiled records. For regulated teams, Sortio also supports local-only inference so sensitive documents never leave the machine during the sort. See the local AI vs cloud AI piece for that tradeoff.',
  },
  {
    question: 'How can I reduce the time staff spend filing and renaming documents?',
    answer:
      'Move the filing and renaming off your staff and onto a rule. Most of that time goes into deciding a name, typing it, and dragging the file to the right folder, repeated thousands of times a year. When a shared Sortio rule reads each file and writes the name and folder from your template automatically, that per-file labor drops to near zero: staff drop documents into an intake folder and the convention is applied for them. The one-time backlog pass is automated too, so the hours are not just reduced going forward, they are recovered on the existing pile as well.',
  },
];

const EnforceNamingConventionBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Enforce a file naming convention', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Enforce a file naming convention',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Enforce a File Naming Convention Your Team Actually Follows',
    description:
      'A written naming policy fails because it depends on people remembering it. Enforce the convention at the point of filing with a shared rule that reads each file and applies your template. Standards, a backlog cleanup plan, and a working Sortio team prompt.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Enforce a File Naming Convention Your Team Actually Follows"
        description="A written naming policy fails because it depends on people remembering it. Enforce the convention at the point of filing with a shared rule that reads each file and applies your template. Standards, backlog cleanup, and a working Sortio team prompt."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="file naming convention, standardize file names across a team, enforce naming convention, document naming standards, team file naming, consistent file names, naming convention people follow"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Enforce a File Naming Convention Your Team Actually Follows
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>9 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            Every team has a naming convention. It lives in a wiki page nobody opens, and the
            shared drive tells the real story: <code>final_v2.pdf</code>,{' '}
            <code>Scan 2026-04-12.pdf</code>, <code>Acme contract (3).docx</code>, and a
            "Miscellaneous" folder with four hundred files in it. The convention is not the
            problem. Enforcement is.
          </p>

          <p>
            This post is the practical version: what a naming convention should look like, why
            the written-policy approach fails every time, and how to move enforcement to the
            point of filing so the standard holds regardless of who is doing the work or how
            close the deadline is.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <FolderTree className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  A naming convention only works when a system applies it, not when people are
                  asked to remember it. Define a template (date, entity, type), then enforce it
                  with a shared rule that reads each file and writes the name and folder
                  automatically. Run a one-time pass over the backlog, then put a standing rule
                  on the intake folder so the drive never drifts again. The result is the OKR
                  most teams actually want: a measurable share of files matching the standard,
                  with no manual policing.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How do you enforce a file naming convention across a team?
          </h2>

          <p>
            You enforce it by removing the human step. A written policy depends on every person
            recalling the exact format under time pressure, and a new hire has no way to know it
            on day one, so compliance decays the moment the team gets busy. The conventions that
            survive are the ones a tool applies at the point of filing. Concretely:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Define one template, not a paragraph of rules.</strong> Pick a single
              field order that every file type can follow (see the next section). One template
              is enforceable; a page of special cases is not.
            </li>
            <li>
              <strong>Encode the template as a shared rule.</strong> In Sortio Teams the rule
              lives in the admin console and is pushed to every seat, so each machine applies
              the identical convention instead of each person interpreting it.
            </li>
            <li>
              <strong>Clean up the backlog once.</strong> Run the rule over the existing drive
              in preview mode, review the proposed names, then apply. This is the step teams
              avoid for years because doing it by hand is a quarter of work.
            </li>
            <li>
              <strong>Put a standing rule on intake.</strong> Point a watch folder at where new
              files land so they are named and routed on arrival, with no one opening an app.
            </li>
            <li>
              <strong>Measure it.</strong> Once names are deterministic you can actually report
              the share of files matching the schema, which is what turns "we have a convention"
              into a key result you can track.
            </li>
          </ol>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            What a good team naming convention looks like
          </h2>

          <p>
            Lead with an ISO date so files sort chronologically in any file browser, then the
            entity the file belongs to, then a short document type, then an optional
            disambiguator. Hyphens inside a field, underscores between fields, so every field
            stays machine-parseable:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`2026-04-12_Acme-Corp_Invoice_4471.pdf
2026-04-15_Bregman-LLC_Engagement-Letter.pdf
2026-04_Northgate-Apts_Lease_Unit-3B.pdf
2026-04-22_Delgado-v-Sato_Motion-to-Dismiss.pdf`}
          </pre>

          <p>
            The reason this order wins: date-first means a folder sorts into a timeline on its
            own; entity-second means you can scan the folder by eye and group by client, matter,
            or property; type-third is what people search for. Avoid spaces, slashes, and
            in-house abbreviations that only one person can decode, because those are exactly the
            parts a human gets wrong under deadline.
          </p>

          <p>
            This is not just an in-house preference. Research-data-management guidance lands in the
            same place:{' '}
            <a
              href="https://guides.library.stanford.edu/data-best-practices"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:underline"
            >
              Stanford University Libraries
            </a>{' '}
            advises that you "be consistent and descriptive in naming and organizing files so that
            it is obvious where to find specific data and what the files contain." Consistency is
            the property that makes a convention useful, and consistency is exactly what people
            cannot hold by hand across a whole team.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why the written-policy approach fails
          </h2>

          <p>
            A naming policy in a document is a request for unpaid, error-prone, repetitive work
            from every employee, forever. It fails for predictable reasons: the person filing at
            5pm before a deadline types whatever is fastest, the new hire never read the wiki,
            two people abbreviate the same client three different ways, and nobody is auditing
            the output. None of these are discipline problems. They are the natural result of
            putting enforcement on humans instead of on the system that writes the file.
          </p>

          <p>
            Moving enforcement to the point of filing fixes all of them at once, because the
            template is applied by software that does not get tired, does not skip the wiki, and
            spells the client name the same way every time.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            A working team rule
          </h2>

          <p>
            Here is a Sortio prompt that reads each incoming file, derives the fields from its
            content, and applies the template above. Save it as a shared team rule and point it
            at the intake folder:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Rename every file to {YYYY-MM-DD}_{Entity}_{DocType}_{Ref}.
- Read the date from the document content; if none, use the file's date.
- Entity is the client, company, matter, or property the file is about.
  Use hyphens, no spaces (e.g. Acme-Corp, Northgate-Apts).
- DocType is one of: Invoice, Receipt, Contract, Engagement-Letter,
  Lease, Statement, Motion, Report. Pick the closest.
- Ref is an invoice number, unit, or matter name if present, else omit.
Then file each into a folder named after the Entity.`}
          </pre>

          <p>
            Run it in preview first. Sortio shows the proposed name and destination for every
            file before anything moves, so you validate the convention on a real sample before
            applying it to the whole drive.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Fixed rule or AI: which to use
          </h2>

          <p>
            Not every file needs an LLM. For a single upstream source that always arrives in the
            same format, promote the convention to a deterministic rule in the AI Rule Builder.
            It does not use your AI allowance at all and never varies. For mixed intake where the
            format differs per sender, use AI sorting so the model derives the fields per
            document. Most teams run a deterministic rule for their highest-volume single-source
            flow and AI sorting for the long tail. The full breakdown is in our piece on{' '}
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="text-sortio-primary hover:underline"
            >
              AI Sort vs Rule Builder
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/business"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Sortio for Teams: shared rules and an admin console
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Local AI vs cloud AI for file organization
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Enforce it on your team's drive</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              Sortio for Teams is $29 per seat per month: shared rules, an admin console, and
              double the Pro AI allowance per seat, pooled across your team. Define your
              convention once, push it to every seat, and let it apply on every file. Start with
              the free tier to test a template on a real folder in preview before you roll it out.
            </p>
            <Link
              to="/business"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              See Sortio for Teams
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EnforceNamingConventionBlogPage;
